import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, X } from 'lucide-react';
import {
  CHANNEL_LABELS,
  NOTICE_CHANNEL_COPY,
  NOTICE_EXTRA_CHANNELS,
  ROLES,
  type NoticeExtraChannel,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  Spinner,
  Switch,
  Textarea,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { uploadMediaAsset } from '@/lib/upload';
import { useRole } from '@/store/auth';
import type { ChildSummary, ClassRoomSummary } from '@/types';

type Audience = 'ALL' | 'CLASS' | 'CHILD';

const AUDIENCE_LABELS: Record<Audience, string> = {
  ALL: '전체',
  CLASS: '반',
  CHILD: '원아 지정',
};

/**
 * 공지 작성 — 명세서 §5.4 (v1.22).
 * 원장은 전체·반·원아, 교사는 담임 반·원아에게 보낸다.
 * 일반 공지도 "문자·이메일로도 안내"를 켜면 앱 알림에 더해 알림톡(문자 폴백)·이메일로 나간다.
 * 긴급 공지는 정책상 둘 다 함께 나가므로 스위치가 잠긴다(§8.1).
 */
export function NoticeEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useRole();
  const isDirector = role === ROLES.DIRECTOR;

  const [audience, setAudience] = useState<Audience>(isDirector ? 'ALL' : 'CLASS');
  const [classRoomId, setClassRoomId] = useState('');
  const [childIds, setChildIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mustRead, setMustRead] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [extraChannels, setExtraChannels] = useState<NoticeExtraChannel[]>([]);
  const [attachments, setAttachments] = useState<{ mediaId: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
    enabled: audience === 'CHILD',
  });

  const effectiveRoomId = classRoomId || rooms.data?.[0]?.id || '';

  const save = useMutation({
    mutationFn: (status: 'DRAFT' | 'PUBLISHED') =>
      api('/notices', {
        method: 'POST',
        body: {
          audience,
          classRoomId: audience === 'CLASS' ? effectiveRoomId : undefined,
          childIds: audience === 'CHILD' ? childIds : undefined,
          title: title.trim(),
          content: content.trim(),
          mustRead,
          isUrgent,
          isPinned,
          extraChannels: isUrgent ? [] : extraChannels,
          mediaIds: attachments.map((item) => item.mediaId),
          status,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notices'] });
      navigate('/notices');
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '공지를 저장하지 못했어요.'),
  });

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files).slice(0, 5 - attachments.length)) {
        const { mediaId } = await uploadMediaAsset(file);
        setAttachments((prev) => [...prev, { mediaId, name: file.name }]);
      }
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const toggleChannel = (channel: NoticeExtraChannel) =>
    setExtraChannels((prev) =>
      prev.includes(channel) ? prev.filter((item) => item !== channel) : [...prev, channel],
    );

  const canSave =
    title.trim().length > 0 &&
    content.trim().length > 0 &&
    (audience !== 'CLASS' || Boolean(effectiveRoomId)) &&
    (audience !== 'CHILD' || childIds.length > 0);

  return (
    <Screen withNav={false}>
      <AppBar title="공지 쓰기" back />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <FieldGroup label="받는 대상" required>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(AUDIENCE_LABELS) as Audience[])
                .filter((option) => option !== 'ALL' || isDirector)
                .map((option) => (
                  <Chip
                    key={option}
                    tone={audience === option ? 'primary' : 'neutral'}
                    selected={audience === option}
                    onClick={() => setAudience(option)}
                  >
                    {AUDIENCE_LABELS[option]}
                  </Chip>
                ))}
            </div>
          </FieldGroup>

          {audience === 'CLASS' && (
            <Field label="반" required>
              {rooms.isPending ? (
                <Spinner />
              ) : (
                <select
                  value={effectiveRoomId}
                  onChange={(e) => setClassRoomId(e.target.value)}
                  className="h-11 w-full rounded-sm border border-line-strong bg-surface px-3 text-base"
                >
                  {rooms.data?.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {audience === 'CHILD' && (
            <FieldGroup label="원아" required hint="선택한 아이의 가정에만 보여요.">
              {children.isPending && <Spinner />}
              <div className="flex flex-wrap gap-2">
                {children.data?.map((child) => (
                  <Chip
                    key={child.id}
                    tone={childIds.includes(child.id) ? 'primary' : 'neutral'}
                    selected={childIds.includes(child.id)}
                    onClick={() =>
                      setChildIds((prev) =>
                        prev.includes(child.id) ? prev.filter((id) => id !== child.id) : [...prev, child.id],
                      )
                    }
                  >
                    {child.name}
                  </Chip>
                ))}
              </div>
            </FieldGroup>
          )}

          <Field label="제목" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 여름 물놀이 안내" maxLength={120} />
          </Field>
          <Field label="내용" required>
            <Textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="가정에 전할 이야기를 적어 주세요." />
          </Field>
        </Card>

        <Card className="flex flex-col gap-3">
          <ToggleRow label="필독" hint="읽음 확인을 집계해요." checked={mustRead} onChange={setMustRead} />
          <ToggleRow
            label="긴급"
            hint={NOTICE_CHANNEL_COPY.urgentNote}
            checked={isUrgent}
            onChange={setIsUrgent}
          />
          <ToggleRow label="상단 고정" checked={isPinned} onChange={setIsPinned} />
        </Card>

        {/* 추가 안내 채널 (§5.4 v1.22) — 앱을 자주 열지 않는 가정에도 닿게 */}
        <Card>
          <p className="font-semibold">{NOTICE_CHANNEL_COPY.sectionTitle}</p>
          <p className="mt-1 text-sm text-ink-sub">{NOTICE_CHANNEL_COPY.hint}</p>
          <div className="mt-3 flex flex-col gap-3">
            {NOTICE_EXTRA_CHANNELS.map((channel) => (
              <div key={channel} className="flex items-center justify-between gap-3">
                <span className="text-base">{CHANNEL_LABELS[channel]}</span>
                <Switch
                  checked={isUrgent || extraChannels.includes(channel)}
                  label={CHANNEL_LABELS[channel]}
                  onChange={() => {
                    if (!isUrgent) toggleChannel(channel);
                  }}
                />
              </div>
            ))}
          </div>
          {isUrgent && <p className="mt-2 text-sm text-ink-sub">{NOTICE_CHANNEL_COPY.urgentNote}</p>}
        </Card>

        <Card>
          <div className="mb-2 flex items-center justify-between">
            <p className="font-semibold">첨부파일</p>
            <label className="tap inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-line-strong px-3 py-2 text-sm font-medium">
              <Paperclip size={15} /> 파일 고르기
              <input
                type="file"
                accept="application/pdf,.hwp,.docx,image/*"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && void handleFiles(e.target.files)}
              />
            </label>
          </div>
          {uploading && <Spinner label="파일을 올리는 중이에요" />}
          {attachments.length === 0 && !uploading && (
            <p className="text-sm text-ink-sub">pdf·hwp·docx·사진을 5개까지 붙일 수 있어요.</p>
          )}
          <ul className="flex flex-col gap-1.5">
            {attachments.map((item) => (
              <li key={item.mediaId} className="flex items-center gap-2 text-sm">
                <Paperclip size={14} className="text-ink-sub" />
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="첨부 빼기"
                  onClick={() => setAttachments((prev) => prev.filter((a) => a.mediaId !== item.mediaId))}
                >
                  <X size={14} />
                </Button>
              </li>
            ))}
          </ul>
        </Card>

        {error && <ErrorNote message={error} />}

        <div className="flex gap-2">
          <Button variant="outline" block disabled={!canSave} loading={save.isPending && save.variables === 'DRAFT'} onClick={() => save.mutate('DRAFT')}>
            임시저장
          </Button>
          <Button block disabled={!canSave} loading={save.isPending && save.variables === 'PUBLISHED'} onClick={() => save.mutate('PUBLISHED')}>
            발행
          </Button>
        </div>
      </div>
    </Screen>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium">{label}</p>
        {hint && <p className="text-sm text-ink-sub">{hint}</p>}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}
